/** @jsx jsx */
import { css, jsx } from '@emotion/react';

import { Grid, Typography2 } from '@coursera/cds-core';

import EnterpriseSkillPathwayCourseActionButton from 'bundles/enterprise-learning-path-description-page/components/EnterpriseSkillPathwayCourseActionButton';
import {
  getEnrollingCourseActionId,
  getEnrollingCourseAssessmentActionId,
  shouldDisableActionButton,
  useSkillPathwayActivelyEnrolling,
} from 'bundles/enterprise-learning-path-description-page/contexts/SkillPathwayActivelyEnrollingContext';

import _t from 'i18n!nls/enterprise-learning-path-description-page';

const styles = {
  container: css`
    margin-top: 20px;
    background-color: var(--cds-color-blue-25);
    padding: var(--cds-spacing-150);
    border-radius: var(--cds-border-radius-100);
  `,
  boldText: css`
    font-weight: 600;
  `,
  buttonContainer: css`
    margin-top: 2px;
  `,
};

type Props = {
  courseTitle: string;
  courseSlug: string;
  courseId: string;
  pathwayId: string;
  isEnrolledInPathway: boolean;
  isEnrolledInCourse: boolean;
  isProgramMember: boolean;
  pathwayCollectionId?: string;
  programId?: string;
  programSlug?: string;
  thirdPartyOrganizationSlug?: string;
  activateProgramAccessModal?: () => void;
};

const SkillPathwayCourseActions = ({
  courseTitle,
  courseSlug,
  courseId,
  pathwayId,
  pathwayCollectionId,
  isEnrolledInPathway,
  isEnrolledInCourse,
  isProgramMember,
  programId,
  programSlug,
  thirdPartyOrganizationSlug,
  activateProgramAccessModal,
}: Props) => {
  const { setCurrentEnrollingAction, currentEnrollingAction } = useSkillPathwayActivelyEnrolling();

  if (!programId || !thirdPartyOrganizationSlug) {
    return null;
  }

  return (
    <div css={styles.container}>
      <Typography2 variant="bodySecondary" component="p">
        <span css={styles.boldText}>
          {_t("Already have proficiency in '#{courseTitle}'? ", {
            courseTitle,
          })}
        </span>
        {_t('You can take the assessment anytime to verify your mastery.')}
      </Typography2>
      <Grid container css={styles.buttonContainer} spacing={12}>
        <Grid item>
          <EnterpriseSkillPathwayCourseActionButton
            isEnrolledInPathway={isEnrolledInPathway}
            isEnrolledInCourse={isEnrolledInCourse}
            isProgramMember={isProgramMember}
            programId={programId}
            courseId={courseId}
            courseSlug={courseSlug}
            pathwayCollectionId={pathwayCollectionId}
            pathwayId={pathwayId}
            programSlug={programSlug}
            thirdPartyOrganizationSlug={thirdPartyOrganizationSlug}
            activateProgramAccessModal={activateProgramAccessModal}
            isEnrollingCallback={setCurrentEnrollingAction}
            disabled={shouldDisableActionButton(getEnrollingCourseActionId(courseId), currentEnrollingAction)}
          />
        </Grid>
        <Grid item>
          <EnterpriseSkillPathwayCourseActionButton
            isEnrolledInPathway={isEnrolledInPathway}
            isEnrolledInCourse={isEnrolledInCourse}
            isProgramMember={isProgramMember}
            programId={programId}
            courseId={courseId}
            courseSlug={courseSlug}
            pathwayCollectionId={pathwayCollectionId}
            pathwayId={pathwayId}
            programSlug={programSlug}
            thirdPartyOrganizationSlug={thirdPartyOrganizationSlug}
            activateProgramAccessModal={activateProgramAccessModal}
            isEnrollingCallback={setCurrentEnrollingAction}
            disabled={shouldDisableActionButton(getEnrollingCourseAssessmentActionId(courseId), currentEnrollingAction)}
            redirectToAssessment
          />
        </Grid>
      </Grid>
    </div>
  );
};

export default SkillPathwayCourseActions;
